/* =========================================================================
   IBC 2.0 — CAKE GUIDE
   sections/ibc-cake-guide.liquid  ·  assets/ibc-cake-guide.css

   The page is a grid of links to the cakes' product pages. That is the
   whole page with no script, and it is complete. This file cancels those
   links and swaps the grid for the cake instead.

   IT IS A PAGE SYSTEM, NOT A MODAL, AND THE HISTORY IS WHY.

   Opening a cake calls `pushState`, so it becomes a real entry in the
   browser's history. Back — the button in the corner, the browser's own,
   the hardware key on a phone — all land on the same `popstate` and all
   return to the grid. This is the change that matters: a modal answers
   Back by leaving the site, and Back is exactly the gesture someone
   reaches for on a phone.

   `pushState` for going in, `replaceState` for stepping sideways between
   cakes. Nine cakes should not cost nine presses of Back to escape, but
   the address bar should always name the cake on screen — for sending to
   someone, or for printing on a second QR code beside one cake.

   SCROLLING IS RESTORED, NOT RESET. The grid's scroll position is
   remembered on the way in and put back on the way out, so leaving a cake
   returns you to the tile you came from rather than to the top of a grid
   you have to find your place in again. Going INTO a cake goes to the top
   of it, because that is what arriving at a page means.
   ========================================================================= */

(function () {
  'use strict';

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function CakeGuide(root) {
    this.root = root;
    this.tiles = toArray(root.querySelectorAll('[data-ibc-cakes-open]'));
    this.cakes = toArray(root.querySelectorAll('[data-ibc-cakes-panel]'));
    this.steps = toArray(root.querySelectorAll('[data-ibc-cakes-step]'));
    this.backs = toArray(root.querySelectorAll('[data-ibc-cakes-back]'));
    this.pos = root.querySelector('[data-ibc-cakes-pos]');

    if (!this.cakes.length) return;

    this.index = 0;
    this.gridScroll = 0;
    this.pushed = false;

    this.bind();
    this.bindGalleries();
    this.paint();
    this.openFromHash();
  }

  CakeGuide.prototype.bind = function () {
    var self = this;

    this.tiles.forEach(function (tile) {
      tile.addEventListener('click', function (event) {
        /* A modified click is the reader asking for a new tab of the
           product page. That is a reasonable thing to want from a link,
           so it is left alone. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button && event.button !== 0) return;

        var i = parseInt(tile.getAttribute('data-ibc-cakes-open'), 10);
        if (isNaN(i)) return;

        event.preventDefault();
        self.open(i);
      });
    });

    this.backs.forEach(function (button) {
      button.addEventListener('click', function () {
        self.back();
      });
    });

    this.steps.forEach(function (button) {
      button.addEventListener('click', function () {
        var by = parseInt(button.getAttribute('data-ibc-cakes-step'), 10);
        if (isNaN(by)) return;
        self.step(by);
      });
    });

    /* ON THE DOCUMENT, gated on the view rather than on where focus
       happens to be. Bound to the section it missed every key pressed
       while focus sat on the body — which is most of the time, since the
       tile that was clicked is hidden the moment the cake opens. The view
       check is what keeps these keys from meaning anything on the grid,
       and the form-field check keeps them out of the header's search. */
    document.addEventListener('keydown', function (event) {
      if (self.view() !== 'cake') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      var tag = event.target && event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (event.key === 'Escape') {
        event.preventDefault();
        self.back();
        return;
      }

      /* Inside the photograph track the arrow keys are the browser's own
         horizontal scroll, which is the more specific meaning. */
      if (event.target && event.target.closest &&
          event.target.closest('[data-ibc-cakes-gallery]')) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.step(1);
      }
    });

    /* One listener for every way of going back. The state object is the
       only thing consulted, so an entry pushed by something else on the
       page cannot be mistaken for one of ours. */
    window.addEventListener('popstate', function (event) {
      var st = event.state;
      if (st && st.ibcCake && st.root === self.root.id) {
        self.pushed = true;
        self.show(st.index, false);
      } else {
        self.pushed = false;
        self.close(false);
      }
    });
  };

  CakeGuide.prototype.view = function () {
    return this.root.getAttribute('data-view') === 'cake' ? 'cake' : 'grid';
  };

  /* Going in: remember where the grid was, add a history entry, and start
     the cake at its top — which is what arriving at a page means. */
  CakeGuide.prototype.open = function (index) {
    if (this.view() !== 'cake') {
      this.gridScroll = window.scrollY || window.pageYOffset || 0;
    }
    this.show(index, true);
  };

  /* Set before anything measures the page: hiding the heading changes
     every offset below it, so `toTop` has to run after this, not before. */
  CakeGuide.prototype.setReading = function (on) {
    if (!document.body) return;
    document.body.classList.toggle('ibc-cakes-reading', !!on);
  };

  CakeGuide.prototype.show = function (index, pushHistory) {
    this.index = index;
    this.root.setAttribute('data-view', 'cake');
    this.setReading(true);
    this.paint();

    if (pushHistory) {
      var url = this.urlFor(index);
      var state = { ibcCake: true, index: index, root: this.root.id };
      try {
        if (this.pushed) {
          window.history.replaceState(state, '', url);
        } else {
          window.history.pushState(state, '', url);
          this.pushed = true;
        }
      } catch (e) {
        /* A browser that refuses the history API still shows the cake. */
      }
    }

    this.toTop();
    this.focusCake();
  };

  CakeGuide.prototype.step = function (by) {
    if (this.view() !== 'cake') return;
    var n = this.cakes.length;
    this.show(((this.index + by) % n + n) % n, true);
  };

  /* Back prefers the browser's own history, so one press undoes one
     arrival however the reader got here. Only when there is no entry of
     ours to go back to does it close the view directly. */
  CakeGuide.prototype.back = function () {
    if (this.pushed && window.history && window.history.back) {
      window.history.back();
      return;
    }
    this.close(true);
  };

  CakeGuide.prototype.close = function (writeHistory) {
    if (this.view() !== 'cake') return;
    this.root.setAttribute('data-view', 'grid');
    this.setReading(false);

    if (writeHistory) {
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (e) {
        /* nothing to do */
      }
      this.pushed = false;
    }

    var y = this.gridScroll;
    var tile = this.tiles[this.index];
    window.scrollTo(0, y);
    if (tile) {
      try {
        tile.focus({ preventScroll: true });
      } catch (e) {
        /* older engines simply do not move focus */
      }
    }
  };

  CakeGuide.prototype.urlFor = function (index) {
    var cake = this.cakes[index];
    var base = window.location.pathname + window.location.search;
    return cake && cake.id ? base + '#' + cake.id : base;
  };

  CakeGuide.prototype.paint = function () {
    var self = this;

    this.cakes.forEach(function (cake, i) {
      var on = i === self.index;
      cake.classList.toggle('is-current', on);
      if (on) self.resetGalleries(cake);
    });

    if (this.pos) {
      this.pos.textContent = String(this.index + 1) + ' of ' + this.cakes.length;
    }
  };

  /* THE HEADER IS FIXED, so scrolling the section's top to the top of the
     viewport puts the Back button underneath it. The header's real height
     is measured rather than assumed — it carries the notch inset on a
     phone, so the token alone is short by the safe-area on exactly the
     devices this page is built for. `--header-h` is the fallback for when
     there is no header in the document at all, and a header that is not
     fixed takes nothing. */
  CakeGuide.prototype.headerOffset = function () {
    var hdr = document.querySelector('.hdr');

    if (hdr) {
      return window.getComputedStyle(hdr).position === 'fixed'
        ? hdr.getBoundingClientRect().height
        : 0;
    }

    var token = parseFloat(
      window.getComputedStyle(document.documentElement).getPropertyValue('--header-h')
    );
    return isNaN(token) ? 0 : token;
  };

  /* The top of the section, not the top of the document — and clear of
     the header, so Back and the photograph are both on screen. */
  CakeGuide.prototype.toTop = function () {
    var top = this.root.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
    var y = top - this.headerOffset() - 12;
    window.scrollTo(0, Math.max(0, Math.round(y)));
  };

  CakeGuide.prototype.focusCake = function () {
    var cake = this.cakes[this.index];
    if (!cake) return;
    try {
      cake.focus({ preventScroll: true });
    } catch (e) {
      /* older engines simply do not move focus */
    }
  };

  /* Every cake opens on its slice — what the tile showed, and what is in
     the counter display. A cake looked at twice would otherwise reopen
     wherever its track was left. */
  CakeGuide.prototype.resetGalleries = function (cake) {
    var self = this;
    toArray(cake.querySelectorAll('[data-ibc-cakes-gallery]')).forEach(function (track) {
      track.scrollLeft = 0;
      self.paintHint(track);
    });
  };

  /* THE SCROLL HINT follows the track, not the buttons — there are no
     buttons. It is read back out of `scrollLeft` on every scroll, so a
     finger, a trackpad, the arrow keys and a snap settling all move it,
     and there is no second copy of "which photograph is showing" to drift
     out of step with the first. */
  CakeGuide.prototype.bindGalleries = function () {
    var self = this;

    toArray(this.root.querySelectorAll('[data-ibc-cakes-gallery]')).forEach(function (track) {
      var pending = false;

      track.addEventListener(
        'scroll',
        function () {
          if (pending) return;
          pending = true;
          window.requestAnimationFrame(function () {
            pending = false;
            self.paintHint(track);
          });
        },
        { passive: true }
      );

      self.paintHint(track);
    });
  };

  CakeGuide.prototype.paintHint = function (track) {
    var media = track.parentElement;
    if (!media) return;

    var hint = media.querySelector('[data-ibc-cakes-hint]');
    if (!hint) return;

    var shots = toArray(track.querySelectorAll('[data-ibc-cakes-shot]'));
    if (shots.length < 2) return;

    /* One slide is one track width, so the nearest whole number of widths
       is the slide on screen. Clamped because a rubber-band overscroll on
       iOS reports past both ends. */
    var width = track.clientWidth || 1;
    var i = Math.round(track.scrollLeft / width);
    if (i < 0) i = 0;
    if (i > shots.length - 1) i = shots.length - 1;

    toArray(hint.querySelectorAll('.ibc-cakes__dot')).forEach(function (dot, n) {
      dot.classList.toggle('is-on', n === i);
    });

    var label = hint.querySelector('[data-ibc-cakes-hint-label]');
    if (label) label.textContent = shots[i].getAttribute('data-ibc-cakes-shot') || '';

    hint.classList.toggle('is-end', i >= shots.length - 1);
  };

  /* A link to one cake opens that cake. An unknown fragment is left
     alone: it may belong to something else on the page. */
  CakeGuide.prototype.openFromHash = function () {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    var target = null;
    try {
      target = this.root.querySelector(hash);
    } catch (e) {
      return;
    }
    if (!target) return;

    var cake = target.closest ? target.closest('[data-ibc-cakes-panel]') : null;
    if (!cake) return;

    var i = this.cakes.indexOf(cake);
    if (i < 0) return;

    /* Arriving ON a cake: the grid was never scrolled, and the entry
       already exists, so this replaces it rather than adding a second. */
    this.gridScroll = 0;
    this.index = i;
    this.root.setAttribute('data-view', 'cake');
    this.setReading(true);
    this.paint();
    try {
      window.history.replaceState(
        { ibcCake: true, index: i, root: this.root.id },
        '',
        this.urlFor(i)
      );
      this.pushed = true;
    } catch (e) {
      /* nothing to do */
    }
    this.toTop();
  };

  /* THE BROWSER'S OWN SCROLL RESTORATION HAS TO BE TURNED OFF, or it
     fights ours. On `popstate` it puts the page back where it thinks it
     was, after this file has already put it where the grid actually was,
     and the two disagreed by a hundred pixels or so depending on which
     entry was being returned to. Ours is the one that knows about the two
     views, so ours wins. Scoped to this document, which is the only one
     with a cake grid in it. */
  function ownScroll() {
    try {
      if (window.history && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (e) {
      /* a browser that will not hand it over simply keeps doing its own */
    }
  }

  function boot() {
    if (!document.querySelector('[data-ibc-cakes]')) return;
    ownScroll();
    toArray(document.querySelectorAll('[data-ibc-cakes]')).forEach(function (root) {
      if (root.dataset.ibcCakesBooted) return;
      root.dataset.ibcCakesBooted = '1';
      new CakeGuide(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor tears a section out and re-inserts it on every
     change, which loses the listeners but not the markup. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);
})();
